import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Organization } from '../types';
import { Plus, Search, MapPin, Building2, Filter, X, SortAsc, ChevronRight, Edit3, Download, Upload } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { OrganizationFormModal } from '../features/organizations/OrganizationFormModal';
import { OrganizationDeleteDialog } from '../features/organizations/OrganizationDeleteDialog';
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse';

const { useNavigate } = ReactRouterDom as any;

const formatCompactAddress = (org: Organization) => {
  const parts = [];
  if (org.city) parts.push(org.city);
  if (org.state) parts.push(org.state);
  return parts.join(', ') || (org.street_address ? 'Address On File' : '—');
};

const OrganizationsList: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  // Persistence Helpers
  const getSaved = (key: string, def: any) => {
    const val = localStorage.getItem(`org_list_${key}`);
    if (val === null) return def;
    try { return JSON.parse(val); } catch { return val; }
  };

  const [searchTerm, setSearchTerm] = useState(() => getSaved('searchTerm', ''));
  const [cityFilter, setCityFilter] = useState(() => getSaved('city', ''));
  const [typeFilter, setTypeFilter] = useState(() => getSaved('type', ''));
  const [sortField, setSortField] = useState<string>(() => getSaved('sortField', 'name'));
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => getSaved('sortDirection', 'asc'));
  const [page, setPage] = useState(() => getSaved('page', 1));
  const [pageSize, setPageSize] = useState(() => getSaved('pageSize', 30));

  // Sync to LocalStorage
  useEffect(() => { localStorage.setItem('org_list_searchTerm', JSON.stringify(searchTerm)); }, [searchTerm]);
  useEffect(() => { localStorage.setItem('org_list_city', JSON.stringify(cityFilter)); }, [cityFilter]);
  useEffect(() => { localStorage.setItem('org_list_type', JSON.stringify(typeFilter)); }, [typeFilter]);
  useEffect(() => { localStorage.setItem('org_list_sortField', JSON.stringify(sortField)); }, [sortField]);
  useEffect(() => { localStorage.setItem('org_list_sortDirection', JSON.stringify(sortDirection)); }, [sortDirection]);
  useEffect(() => { localStorage.setItem('org_list_page', JSON.stringify(page)); }, [page]);
  useEffect(() => { localStorage.setItem('org_list_pageSize', JSON.stringify(pageSize)); }, [pageSize]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 300,
    category: 150,
    location: 250,
    actions: 120
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<{ id: string, name: string } | null>(null);

  const isAdmin = profile?.role === 'system_admin';

  const { data: orgsData, isLoading } = useQuery({
    queryKey: ['organizations', searchTerm, cityFilter, typeFilter, sortField, sortDirection, page, pageSize],
    queryFn: async () => {
      let query = supabase.from('organizations').select('*, organization_types(id, label)', { count: 'exact' });
      
      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }
      if (cityFilter) {
        query = query.ilike('city', `%${cityFilter}%`);
      }
      if (typeFilter) {
        query = query.eq('org_type_id', typeFilter);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await query
        .order(sortField === 'category' ? 'org_type_id' : sortField, { ascending: sortDirection === 'asc' })
        .range(from, to);

      if (error) throw error;
      return { organizations: data as Organization[], total: count || 0 };
    }
  });

  const orgs = orgsData?.organizations || [];
  const totalCount = orgsData?.total || 0;

  const { data: orgTypes } = useQuery({
    queryKey: ['org-types-filter'],
    queryFn: async () => {
      const { data } = await supabase.from('organization_types').select('*');
      return data || [];
    }
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleResize = (column: string, width: number) => {
    setColumnWidths(prev => ({ ...prev, [column]: Math.max(100, width) }));
  };

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) return <SortAsc size={10} className="ml-1 opacity-20" />;
    return sortDirection === 'asc' 
      ? <SortAsc size={10} className="ml-1 text-blue-600" /> 
      : <SortAsc size={10} className="ml-1 text-blue-600 rotate-180" />;
  };

  const handleExport = () => {
    const data = orgs.map(o => ({
      'Organization Name': o.name,
      'Type': (o as any).organization_types?.label || 'General',
      'Website': o.website || '—',
      'City': o.city || '—',
      'State': o.state || '—',
      'Zip': o.zip_code || '—'
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `hfli_organizations_${new Date().toISOString().slice(0, 10)}.csv`);
    link.click();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">
            Organization directory <span className="text-slate-300 ml-1">
              {searchTerm || cityFilter || typeFilter ? `(Showing ${orgs.length} of ${totalCount})` : `(${totalCount})`}
            </span>
          </h1>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">Partners & Affiliate Organizations</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport} icon={<Download size={18} />}>Export CSV</Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/import?type=orgs')} icon={<Upload size={18} />}>Import CSV</Button>
          )}
          <Button 
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus size={18} />}
            className="px-8 py-4 shadow-lg shadow-blue-500/10"
          >
            New Organization
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-wrap gap-4 items-center justify-between bg-slate-50/30">
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Search Name..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>

            <div className="relative w-full md:w-40">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="City..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
                value={cityFilter}
                onChange={(e) => { setCityFilter(e.target.value); setPage(1); }}
              />
            </div>

            <div className="relative w-full md:w-48">
              <select 
                value={typeFilter} 
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="w-full pl-4 pr-10 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm appearance-none"
              >
                <option value="">All Category Types</option>
                {orgTypes?.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={12} />
            </div>
            
            {(cityFilter || typeFilter || searchTerm) && (
              <button onClick={() => { setCityFilter(''); setTypeFilter(''); setSearchTerm(''); setPage(1); }} className="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline px-2">
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 text-[8px] font-black uppercase tracking-[0.2em] select-none">
                <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.name }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('name')}>
                    Name <SortIndicator field="name" />
                  </div>
                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                    const startX = e.pageX;
                    const startWidth = columnWidths.name;
                    const onMouseMove = (moveEvent: MouseEvent) => {
                      handleResize('name', startWidth + (moveEvent.pageX - startX));
                    };
                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }} />
                </th>
                <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.category }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('category')}>
                    Category <SortIndicator field="category" />
                  </div>
                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                    const startX = e.pageX;
                    const startWidth = columnWidths.category;
                    const onMouseMove = (moveEvent: MouseEvent) => {
                      handleResize('category', startWidth + (moveEvent.pageX - startX));
                    };
                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }} />
                </th>
                <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.location }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('location')}>
                    Location <SortIndicator field="location" />
                  </div>
                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                    const startX = e.pageX;
                    const startWidth = columnWidths.location;
                    const onMouseMove = (moveEvent: MouseEvent) => {
                      handleResize('location', startWidth + (moveEvent.pageX - startX));
                    };
                    const onMouseUp = () => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }} />
                </th>
                <th className="px-6 py-2.5 text-right" style={{ width: columnWidths.actions }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white">
              {isLoading ? (
                [1, 2, 3, 4, 5, 6].map(i => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={4} className="px-6 py-3"><div className="h-3 bg-slate-50 rounded w-full"></div></td>
                  </tr>
                ))
              ) : orgs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-20 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">No matching organizations</td>
                </tr>
              ) : (
                orgs.map((org: any) => (
                  <tr 
                    key={org.id} 
                    className="hover:bg-slate-50/80 transition-all group cursor-pointer"
                    onClick={() => navigate(`/organizations/${org.id}`)}
                  >
                    <td className="px-6 py-2.5 overflow-hidden">
                      <p className="font-bold text-slate-900 uppercase text-[11px] tracking-tight break-words">
                        {org.name}
                      </p>
                    </td>
                    <td className="px-6 py-2.5 overflow-hidden">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100 text-[8px] font-black uppercase tracking-widest truncate">
                        {org.organization_types?.label || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-2.5 overflow-hidden">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight truncate block">
                        {formatCompactAddress(org)}
                      </span>
                    </td>
                    <td className="px-6 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5 opacity-30 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setEditingOrg(org); }} 
                          className="p-1 bg-white border border-slate-200 rounded text-slate-400 hover:text-slate-900 hover:border-slate-900 transition-all"
                        >
                          <Edit3 size={11} />
                        </button>
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setDeletingOrg({ id: org.id, name: org.name });
                          }} 
                          className="p-1 bg-white border border-slate-200 rounded text-slate-400 hover:text-rose-600 hover:border-rose-600 transition-all"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Records per page</span>
              <select 
                value={pageSize} 
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="bg-white border border-slate-200 rounded px-2 py-1 text-[10px] font-bold outline-none focus:ring-2 focus:ring-blue-100"
              >
                {[30, 50, 100, 500, 1000].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Showing {Math.min(totalCount, (page - 1) * pageSize + 1)} - {Math.min(totalCount, page * pageSize)} of {totalCount}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1} 
              onClick={() => setPage((p: number) => p - 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Prev
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = page;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded text-[10px] font-black transition-all ${page === pageNum ? 'bg-slate-900 text-[#FFDB00]' : 'bg-white border border-slate-200 text-slate-400 hover:border-slate-900 hover:text-slate-900'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === totalPages || totalPages === 0} 
              onClick={() => setPage((p: number) => p + 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {(isAddModalOpen || editingOrg) && (
        <OrganizationFormModal 
          organization={editingOrg} 
          onClose={() => { setIsAddModalOpen(false); setEditingOrg(null); }} 
        />
      )}

      {deletingOrg && (
        <OrganizationDeleteDialog 
          orgId={deletingOrg.id}
          orgName={deletingOrg.name}
          onClose={() => setDeletingOrg(null)}
        />
      )}
    </div>
  );
};

export default OrganizationsList;